import { selectAvailableCapabilityCatalog, selectAvailableModules } from '../../projection/capabilities/availability.js';
import { selectLatestSourcingContext } from '../../projection/sources/context.js';
import React, { useCallback, useMemo } from 'react';
import { ContextGauge } from './ContextGauge.jsx';
import { useComposerFocus } from './useFocus.js';
import ComposerSectionNode from './Section.jsx';
import { CHAT_CONTENT_MAX_WIDTH, RESOLVED_UI_LABELS } from '../config/constants.js';
import { useAppActionErrorGuards } from '../workspace/useActionErrors.js';
import { useComposerAttachmentSync } from './useAttachments.js';
import { useSendWithContextRefs } from './useSend.js';
import { hasBlockingComposerAttachments } from '../../application/composer/attachment-send-refs.js';
import { useComposerChooserHandlers } from './useChooserHandlers.js';
import { useUserDisplayPolicy } from '../config/useUserDisplayPolicy.js';
import { selectActiveTrackView } from '../../projection/track/trackViewSelector.js';
import { selectComposerInputTip } from '../../projection/composer/composerInputTipSelector.js';
import { normalizeComposerModeOptions, resolveComposerModeLabel } from './mode-options.js';
import { useComposerActiveArtifactTarget } from './useActiveArtifactTarget.js';
import { normalizeCapabilityCatalog } from '../../projection/capabilities/catalog.js';
import { buildContextSurfaceLines, buildSourceContextQueue, resolveSourceContextGaugeProjection } from '../../projection/sources/context-queue.js';
import * as queueFormatters from '../locale/context-queue-labels.js';
import { useArtifacts } from '../../application/artifacts/provider';
import { useComposerContext } from '../../application/composer/provider';
import { useOperations } from '../../application/operations/provider';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

function ComposerSectionWithActiveTarget({ activeArtifactTarget, ...props }) {
  return (
    <ComposerSectionNode
      {...props}
      activeArtifactTargetChip={activeArtifactTarget?.chip || null}
    />
  );
}

export function useComposerController(controllerOptions) {
  const { userId, projectId, activeSessionId, viewModel, apiReadinessGate, blockedApiError, sourceItems, sourceUploadLoading, sourceUploadProgressItems, activeWorkspaceTab, showCanvasPanel, canvasActiveTabKey, canvasTargetFocusKey, setActiveWorkspaceTab, themeTokens, resolvedUILabels, displayPolicy, composerModeOptions, capabilityCatalog, modulePromptRegistry } = { sourceUploadLoading: false, sourceUploadProgressItems: [], showCanvasPanel: false, canvasActiveTabKey: '', canvasTargetFocusKey: 0, resolvedUILabels: RESOLVED_UI_LABELS, displayPolicy: {}, composerModeOptions: [], capabilityCatalog: [], modulePromptRegistry: [], ...Object.fromEntries(Object.entries(Object(controllerOptions)).filter(([, value]) => value !== undefined)) };
  const { evidenceStrictMode, evidenceHitScoreThreshold } = useUserDisplayPolicy({ userId, projectId });

  const { composerModeKey, setComposerModeKey, savedIntakeCanvasContent, setSavedIntakeCanvasContent } = useComposerContext();
  const { status: artifactSaveStatus, interactionContext } = useArtifacts();

  const composerFileList = useMemo(() => (choose(Array.isArray(viewModel.composer.fileList), () => (viewModel.composer.fileList), () => ([]))), [viewModel.composer.fileList]);

  const {
    composerFileListRef,
    hasNonReadyAttachments,
    readyAttachmentRefs,
  } = useComposerAttachmentSync({
    composerFileList,
    onSyncFileList: viewModel.composer.onSyncFileList,
  });
  const sendDisabledByAttachments = useMemo(() => hasBlockingComposerAttachments(composerFileList), [composerFileList]);
  const latestSourcingContext = useMemo(
    () => selectLatestSourcingContext(viewModel.timeline.items),
    [viewModel.timeline.items],
  );
  const { activeArtifactTarget, dismissActiveArtifactTarget } = useComposerActiveArtifactTarget({ activeSessionId, canvasActiveTabKey, canvasTabLabelsByKey: { requirement: resolvedUILabels.canvas_tab_result, analysis: resolvedUILabels.canvas_tab_analysis, sourcing: resolvedUILabels.canvas_tab_sourcing, evidence: resolvedUILabels.canvas_tab_evidence }, canvasTargetFocusKey, composerModeKey, sessionContext: interactionContext, showCanvasPanel, timelineItems: viewModel.timeline.items });



  const { actionInput, inputResponse } = useOperations();
  const preparedComposer = useMemo(() => ({ ...viewModel.composer, onSend: inputResponse.onSend }),
    [viewModel.composer, inputResponse.onSend]);

  const baseHandleSendWithSourceRefs = useSendWithContextRefs({
    composerModeKey,
    sourceItems,
    composer: preparedComposer,
    activeWorkspaceTab,
    setActiveWorkspaceTab,
    composerFileListRef,
    hasNonReadyAttachments: firstTruthy(() => (sendDisabledByAttachments), () => (hasNonReadyAttachments)),
    readyAttachmentRefs,
    latestSourcingQuery: latestSourcingContext.latestSourcingQuery,
    latestSourcingResultIds: latestSourcingContext.latestSourcingResultIds,
    savedIntakeCanvasContent,
    activeArtifactTarget,
  });

  const handleSendWithSourceRefs = baseHandleSendWithSourceRefs;

  const actionGuards = useAppActionErrorGuards({
    blocked: apiReadinessGate.blocked,
    blockedError: blockedApiError,
    sessionListError: firstTruthy(() => (apiReadinessGate.error), () => (viewModel.sessionList.error), () => (null)),
    composerError: firstTruthy(() => (inputResponse.error), () => (actionInput.error), () => (artifactSaveStatus.result?.error), () => (viewModel.composer.error), () => (null)),
    scopeKey: `${activeSessionId}:${viewModel.composer.inputProjection.requestKey ?? ''}`,
    onCreateProject: viewModel.sessionList.onCreateProject,
    onCreateSession: viewModel.sessionList.onCreateSession,
    onSend: handleSendWithSourceRefs,
    onRespondInput: inputResponse.onRespondInput,
    onRetry: inputResponse.onRetry,
  });

  const inputState = {
    inputValue: viewModel.composer.value,
    fileList: viewModel.composer.fileList,
    handleInputChange: viewModel.composer.onChange,
    handleRemoveFile: viewModel.composer.onRemoveFile,
  };
  const composerBusy = Boolean(
    firstTruthy(() => (viewModel.composer.loading), () => (viewModel.composer.submitting), () => (actionInput.pending))
  );
  const composerFocusKey = `${String(firstTruthy(() => (projectId), () => (''))).trim()}::${String(firstTruthy(() => (activeSessionId), () => (''))).trim()}`;
  const resolvedComposerModeOptions = useMemo(
    () => normalizeComposerModeOptions(composerModeOptions),
    [composerModeOptions],
  );
  const resolvedCapabilityCatalog = useMemo(
    () => normalizeCapabilityCatalog(selectAvailableCapabilityCatalog(capabilityCatalog, viewModel.messageCapabilities)),
    [capabilityCatalog, viewModel.messageCapabilities],
  );

  const activeTrackView = useMemo(
    () => selectActiveTrackView({ timelineItems: viewModel.timeline.items }),
    [viewModel.timeline.items],
  );
  const effectiveComposerChooser = inputResponse.projection.composerChooser;
  const composerFocusBlocked = Boolean(firstTruthy(() => (composerBusy), () => (effectiveComposerChooser)));
  const composerInputTip = useMemo(
    () => selectComposerInputTip({
      inputValue: viewModel.composer.value,
      activeModeKey: composerModeKey,
      modulePromptRegistry: selectAvailableModules(modulePromptRegistry, viewModel.messageCapabilities),
    }),
    [composerModeKey, modulePromptRegistry, viewModel.composer.value, viewModel.messageCapabilities],
  );

  const composerInputRef = useComposerFocus({ focusKey: composerFocusKey, blocked: composerFocusBlocked });

  const { onComposerActionSelect } = useComposerChooserHandlers({
    onChange: viewModel.composer.onChange,
  });

  const runtimePreferences = useMemo(() => ({
    evidence_min_score: choose(Number.isFinite(Number(evidenceHitScoreThreshold)), () => (Number(evidenceHitScoreThreshold)), () => (null)),
    evidence_strict_display: evidenceStrictMode === true,
  }), [evidenceHitScoreThreshold, evidenceStrictMode]);

  const contextUsageLines = choose(Array.isArray(Object(viewModel.contextUsage).lines), () => (viewModel.contextUsage.lines), () => ([]));
  const contextActivity = firstTruthy(() => (Object(viewModel.contextUsage).contextActivity), () => (null));
  const sourceContextQueue = buildSourceContextQueue({ sourceItems, sourceUploadLoading, sourceUploadProgressItems }, queueFormatters);
  const fallbackContextWindowPercent = viewModel.contextUsage?.windowPercent ?? null;
  const contextGaugeProjection = resolveSourceContextGaugeProjection({ backendActivity: contextActivity, backendPercent: fallbackContextWindowPercent, sourceContextQueue });
  const contextGaugeActivity = contextGaugeProjection.activity;
  const contextWindowPercent = contextGaugeProjection.percent;
  const activeContextSurfaceLines = buildContextSurfaceLines({ activeWorkspaceTab, showCanvasPanel, canvasActiveTabKey, canvasTabLabelsByKey: { requirement: resolvedUILabels.canvas_tab_result, analysis: resolvedUILabels.canvas_tab_analysis, sourcing: resolvedUILabels.canvas_tab_sourcing, evidence: resolvedUILabels.canvas_tab_evidence }, sourceContextQueue, backendActivity: contextActivity }, queueFormatters);
  const trackGaugeLines = choose(activeTrackView, () => ([
    `当前轨道：${activeTrackView.title}`,
    ...(activeTrackView.currentStepTitle ? [`当前步骤：${activeTrackView.currentStepTitle}`] : []),
  ]), () => ([]));
  const contextGaugeLines = [
    resolveComposerModeLabel(composerModeKey, resolvedComposerModeOptions),
    ...trackGaugeLines,
    ...activeContextSurfaceLines,
    ...sourceContextQueue.lines,
    ...contextUsageLines,
  ];
  const handleComposerModeChanged = useCallback((modeKey) => {
    const nextModeKey = firstTruthy(() => (String(modeKey || 'auto').trim()), () => ('auto'));
    setComposerModeKey(nextModeKey);
  }, [setComposerModeKey]);
  const contextGaugeContent = <ContextGauge lines={contextGaugeLines} />;

  const composerNode = (
    <ComposerSectionWithActiveTarget
      themeTokens={themeTokens}
      contentMaxWidth={CHAT_CONTENT_MAX_WIDTH}
      streamLoading={viewModel.composer.loading}
      sendSubmitting={firstTruthy(() => (viewModel.composer.submitting), () => (actionInput.pending))}
      sendDisabled={sendDisabledByAttachments || !inputResponse.available}
      resolvedUILabels={resolvedUILabels}
      modeSwitchOptions={resolvedComposerModeOptions}
      capabilityCatalog={resolvedCapabilityCatalog}
      inputState={inputState}
      onAttachFiles={viewModel.composer.onAttachFiles}
      onSend={actionGuards.handleSend}
      onAbort={viewModel.composer.onAbort}
      effectiveComposerChooser={effectiveComposerChooser}
      inputProjection={inputResponse.projection}
      onRespondInput={actionGuards.handleRespondInput}
      onComposerActionSelect={onComposerActionSelect}
      onComposerChooserDismiss={inputResponse.onComposerChooserDismiss}
      canonicalActionEntries={[]}
      initialModeKey={composerModeKey}
      onModeChanged={handleComposerModeChanged}
      userId={userId} displayPolicy={displayPolicy}
      shouldRenderModeSuggestion
      modeSuggestion={composerInputTip?.modeSuggestion || null}
      modeSuggestionAction={composerInputTip?.modeSuggestionAction || null}
      modeSuggestionActions={composerInputTip?.modeSuggestionActions || []}
      runtimePreferences={runtimePreferences}
      contextGaugePopoverContent={contextGaugeContent}
      contextGaugeHeadline={firstTruthy(() => (activeContextSurfaceLines[0]), () => (contextGaugeProjection.headline), () => (''))}
      contextGaugeActivity={contextGaugeActivity}
      contextWindowPercent={contextWindowPercent}
      activeArtifactTarget={activeArtifactTarget}
      onActiveArtifactTargetDismiss={dismissActiveArtifactTarget}
      composerInputRef={composerInputRef}
    />
  );

  return {
    actionGuards,
    inputState,
    composerNode,
    composerModeKey,
    evidenceStrictMode,
    evidenceHitScoreThreshold,
    handleSendWithSourceRefs,
    setSavedIntakeCanvasContent,
  };
}
export default useComposerController;
